import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  ServiceTicketType,
  Note,
  ActionLog,
  TechnicianType,
  ChecklistTemplateType,
  ServiceTicketChecklistType,
  ServiceTicketsStats,
  ServiceOrganizationListItemType,
} from '../../types/service-calls/service-calls.type';
import {
  deleteServiceTicket,
  getServiceTicket,
  getServiceTicketsList,
  getServiceTicketsStats,
  patchServiceTicket,
  postServiceTicket,
  postServiceTicketComment,
  postServiceTicketNote,
  getServiceTicketNotes,
  getServiceTicketActionLogs,
  postServiceTicketTechnician,
  deleteServiceTicketTechnician,
  updateServiceTicketScheduledDateTime,
  deleteServiceTicketScheduledDateTime,
  addServiceTicketContact,
  updateServiceTicketContact,
  deleteServiceTicketContact,
  addServiceTicketCCUser,
  removeServiceTicketCCUser,
  getTechnicians,
  getTechnicianServiceZipCodes,
  addTechnician,
  removeTechnician,
  updateTechnician,
  getServiceOrganizations,
  addServiceOrganization,
  removeServiceOrganization,
  updateServiceOrganization,
  cancelServiceTicket,
  addServiceTicketAssignedUser,
  removeServiceTicketAssignedUser,
  updateServiceTicketDetails,
  getChecklistTemplates,
  getChecklistTemplate,
  createChecklistTemplate,
  updateChecklistTemplate,
  deleteChecklistTemplate,
  setDefaultChecklistTemplate,
  getServiceTicketChecklist,
  updateServiceTicketChecklistItem,
  completeServiceTicketChecklist,
} from './serviceCalls.action';

export interface ServiceCallsState {
  serviceTicketsList: null | PaginationType<ServiceTicketType>;
  serviceTicketsListFetchStatus: FETCH_STATUS;
  serviceTicketsStats: ServiceTicketsStats | null;
  serviceTicketsStatsFetchStatus: FETCH_STATUS;
  serviceTicketsRouteParams: RouteParamsType;
  serviceTicketDetails: null | ServiceTicketType;
  serviceTicketDetailsFetchStatus: FETCH_STATUS;
  serviceTicketDetailsPostStatus: FETCH_STATUS;
  serviceTicketDetailsPatchStatus: FETCH_STATUS;
  serviceTicketDetailsDeleteStatus: FETCH_STATUS;
  serviceTicketCancelStatus: FETCH_STATUS;
  serviceTicketCommentPostStatus: FETCH_STATUS;
  serviceTicketNotePostStatus: FETCH_STATUS;
  serviceTicketNotes: Note[];
  serviceTicketNotesFetchStatus: FETCH_STATUS;
  serviceTicketActionLogs: ActionLog[];
  serviceTicketActionLogsFetchStatus: FETCH_STATUS;
  serviceTicketTechnicianPostStatus: FETCH_STATUS;
  serviceTicketScheduledDateTimeUpdateStatus: FETCH_STATUS;
  serviceTicketScheduledDateTimeDeleteStatus: FETCH_STATUS;
  serviceTicketContactAddStatus: FETCH_STATUS;
  serviceTicketContactUpdateStatus: FETCH_STATUS;
  serviceTicketContactDeleteStatus: FETCH_STATUS;
  serviceTicketCCUserAddStatus: FETCH_STATUS;
  serviceTicketCCUserRemoveStatus: FETCH_STATUS;
  technicians: null | PaginationType<TechnicianType>;
  techniciansFetchStatus: FETCH_STATUS;
  techniciansRouteParams: RouteParamsType;
  technicianServiceZipCodes: string[];
  technicianServiceZipCodesFetchStatus: FETCH_STATUS;
  serviceOrganizationsRouteParams: RouteParamsType;
  technicianPostStatus: FETCH_STATUS;
  technicianRemoveStatus: FETCH_STATUS;
  technicianUpdateStatus: FETCH_STATUS;
  serviceOrganizations: null | PaginationType<ServiceOrganizationListItemType>;
  serviceOrganizationsFetchStatus: FETCH_STATUS;
  serviceOrganizationPostStatus: FETCH_STATUS;
  serviceOrganizationRemoveStatus: FETCH_STATUS;
  serviceOrganizationUpdateStatus: FETCH_STATUS;
  serviceTicketAssignedUserAddStatus: FETCH_STATUS;
  serviceTicketAssignedUserRemoveStatus: FETCH_STATUS;
  serviceTicketDetailsUpdateStatus: FETCH_STATUS;
  checklistTemplates: ChecklistTemplateType[] | null;
  checklistTemplatesFetchStatus: FETCH_STATUS;
  checklistTemplatesRouteParams: RouteParamsType;
  checklistTemplateMutateStatus: FETCH_STATUS;
  selectedChecklistTemplate: ChecklistTemplateType | null;
  serviceTicketChecklist: ServiceTicketChecklistType | null;
  serviceTicketChecklistFetchStatus: FETCH_STATUS;
  serviceTicketChecklistMutateStatus: FETCH_STATUS;
}

export const initialServiceTicketsRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
  status: '',
  serviceVariant: '',
  service_organization_ids: '',
  atRisk: '',
  siteReadinessStatus: '',
};

export const initialTechniciansRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
  rehirable: '',
  serviceOrganizationIds: '',
  serviceZipCodes: '',
};

export const initialServiceOrganizationsRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

export const initialChecklistTemplatesRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
  serviceVariant: '',
};

const INIT_STATE: ServiceCallsState = {
  serviceTicketsRouteParams: initialServiceTicketsRouteParams,
  serviceTicketsList: null,
  serviceTicketsListFetchStatus: FETCH_STATUS.NULL,
  serviceTicketsStats: null,
  serviceTicketsStatsFetchStatus: FETCH_STATUS.NULL,
  serviceTicketDetails: null,
  serviceTicketDetailsFetchStatus: FETCH_STATUS.NULL,
  serviceTicketDetailsPostStatus: FETCH_STATUS.NULL,
  serviceTicketDetailsPatchStatus: FETCH_STATUS.NULL,
  serviceTicketDetailsDeleteStatus: FETCH_STATUS.NULL,
  serviceTicketCancelStatus: FETCH_STATUS.NULL,
  serviceTicketCommentPostStatus: FETCH_STATUS.NULL,
  serviceTicketNotePostStatus: FETCH_STATUS.NULL,
  serviceTicketNotes: [],
  serviceTicketNotesFetchStatus: FETCH_STATUS.NULL,
  serviceTicketActionLogs: [],
  serviceTicketActionLogsFetchStatus: FETCH_STATUS.NULL,
  serviceTicketTechnicianPostStatus: FETCH_STATUS.NULL,
  serviceTicketScheduledDateTimeUpdateStatus: FETCH_STATUS.NULL,
  serviceTicketScheduledDateTimeDeleteStatus: FETCH_STATUS.NULL,
  serviceTicketContactAddStatus: FETCH_STATUS.NULL,
  serviceTicketContactUpdateStatus: FETCH_STATUS.NULL,
  serviceTicketContactDeleteStatus: FETCH_STATUS.NULL,
  serviceTicketCCUserAddStatus: FETCH_STATUS.NULL,
  serviceTicketCCUserRemoveStatus: FETCH_STATUS.NULL,
  technicians: null,
  techniciansFetchStatus: FETCH_STATUS.NULL,
  techniciansRouteParams: initialTechniciansRouteParams,
  technicianServiceZipCodes: [],
  technicianServiceZipCodesFetchStatus: FETCH_STATUS.NULL,
  serviceOrganizationsRouteParams: initialServiceOrganizationsRouteParams,
  technicianPostStatus: FETCH_STATUS.NULL,
  technicianRemoveStatus: FETCH_STATUS.NULL,
  technicianUpdateStatus: FETCH_STATUS.NULL,
  serviceOrganizations: null,
  serviceOrganizationsFetchStatus: FETCH_STATUS.NULL,
  serviceOrganizationPostStatus: FETCH_STATUS.NULL,
  serviceOrganizationRemoveStatus: FETCH_STATUS.NULL,
  serviceOrganizationUpdateStatus: FETCH_STATUS.NULL,
  serviceTicketAssignedUserAddStatus: FETCH_STATUS.NULL,
  serviceTicketAssignedUserRemoveStatus: FETCH_STATUS.NULL,
  serviceTicketDetailsUpdateStatus: FETCH_STATUS.NULL,
  checklistTemplates: null,
  checklistTemplatesFetchStatus: FETCH_STATUS.NULL,
  checklistTemplatesRouteParams: initialChecklistTemplatesRouteParams,
  checklistTemplateMutateStatus: FETCH_STATUS.NULL,
  selectedChecklistTemplate: null,
  serviceTicketChecklist: null,
  serviceTicketChecklistFetchStatus: FETCH_STATUS.NULL,
  serviceTicketChecklistMutateStatus: FETCH_STATUS.NULL,
};

export const serviceCallsSlice = createSlice({
  name: 'service-calls',
  initialState: INIT_STATE,
  reducers: {
    setServiceTicketsList: (state, action) => {
      state.serviceTicketsList = action.payload;
    },
    setServiceTicketsListFetchStatus: (state, action) => {
      state.serviceTicketsListFetchStatus = action.payload;
    },
    setServiceTicketDetails: (state, action) => {
      state.serviceTicketDetails = action.payload;
    },
    setServiceTicketDetailsFetchStatus: (state, action) => {
      state.serviceTicketDetailsFetchStatus = action.payload;
    },
    setServiceTicketCommentPostStatus: (state, action) => {
      state.serviceTicketCommentPostStatus = action.payload;
    },
    setServiceTicketNotePostStatus: (state, action) => {
      state.serviceTicketNotePostStatus = action.payload;
    },
    setServiceTicketNotes: (state, action) => {
      state.serviceTicketNotes = action.payload;
    },
    setServiceTicketNotesFetchStatus: (state, action) => {
      state.serviceTicketNotesFetchStatus = action.payload;
    },
    setServiceTicketActionLogs: (state, action) => {
      state.serviceTicketActionLogs = action.payload;
    },
    setServiceTicketActionLogsFetchStatus: (state, action) => {
      state.serviceTicketActionLogsFetchStatus = action.payload;
    },
    setServiceTicketTechnicianPostStatus: (state, action) => {
      state.serviceTicketTechnicianPostStatus = action.payload;
    },
    setServiceTicketScheduledDateTimeUpdateStatus: (state, action) => {
      state.serviceTicketScheduledDateTimeUpdateStatus = action.payload;
    },
    setServiceTicketScheduledDateTimeDeleteStatus: (state, action) => {
      state.serviceTicketScheduledDateTimeDeleteStatus = action.payload;
    },
    setTechnicians: (state, action) => {
      state.technicians = action.payload;
    },
    setTechniciansFetchStatus: (state, action) => {
      state.techniciansFetchStatus = action.payload;
    },
    setServiceOrganizations: (state, action) => {
      state.serviceOrganizations = action.payload;
    },
    setServiceOrganizationsFetchStatus: (state, action) => {
      state.serviceOrganizationsFetchStatus = action.payload;
    },
    setServiceOrganizationsRouteParams: (state, action) => {
      state.serviceOrganizationsRouteParams = { ...state.serviceOrganizationsRouteParams, ...action.payload };
    },
    setChecklistTemplatesList: (state, action) => {
      state.checklistTemplates = action.payload ?? [];
    },
    setChecklistTemplatesListFetchStatus: (state, action) => {
      state.checklistTemplatesFetchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'service-calls') {
        state.serviceTicketsRouteParams = { ...state.serviceTicketsRouteParams, ...payload.data };
      }
      if (payload.reducer === 'technicians') {
        state.techniciansRouteParams = { ...state.techniciansRouteParams, ...payload.data };
      }
      if (payload.reducer === 'service-organizations') {
        state.serviceOrganizationsRouteParams = { ...state.serviceOrganizationsRouteParams, ...payload.data };
      }
      if (payload.reducer === 'checklist-templates') {
        state.checklistTemplatesRouteParams = { ...state.checklistTemplatesRouteParams, ...payload.data };
      }
    });

    // GET SERVICE TICKETS LIST
    builder.addCase(getServiceTicketsList.pending, state => {
      state.serviceTicketsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceTicketsList.fulfilled, (state, action) => {
      state.serviceTicketsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketsList = action.payload;
    });
    builder.addCase(getServiceTicketsList.rejected, (state, action) => {
      state.serviceTicketsListFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    builder.addCase(getServiceTicketsStats.pending, state => {
      state.serviceTicketsStatsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceTicketsStats.fulfilled, (state, action) => {
      state.serviceTicketsStatsFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketsStats = action.payload;
    });
    builder.addCase(getServiceTicketsStats.rejected, state => {
      state.serviceTicketsStatsFetchStatus = FETCH_STATUS.REJECTED;
      state.serviceTicketsStats = {};
    });

    // GET SERVICE TICKET DETAILS
    builder.addCase(getServiceTicket.pending, state => {
      state.serviceTicketDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceTicket.fulfilled, (state, action) => {
      state.serviceTicketDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
    });
    builder.addCase(getServiceTicket.rejected, (state, action) => {
      state.serviceTicketDetailsFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // CREATE SERVICE TICKET
    builder.addCase(postServiceTicket.pending, state => {
      state.serviceTicketDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postServiceTicket.fulfilled, (state, action) => {
      state.serviceTicketDetailsPostStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Service ticket created successfully');
      });
    });
    builder.addCase(postServiceTicket.rejected, (state, action) => {
      state.serviceTicketDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // UPDATE SERVICE TICKET
    builder.addCase(patchServiceTicket.pending, state => {
      state.serviceTicketDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchServiceTicket.fulfilled, (state, action) => {
      state.serviceTicketDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Service ticket updated successfully');
      });
    });
    builder.addCase(patchServiceTicket.rejected, (state, action) => {
      state.serviceTicketDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // DELETE SERVICE TICKET
    builder.addCase(deleteServiceTicket.pending, state => {
      state.serviceTicketDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteServiceTicket.fulfilled, state => {
      state.serviceTicketDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = null;
      setTimeout(() => {
        snackbarUtils.success('Service ticket deleted successfully');
      });
    });
    builder.addCase(deleteServiceTicket.rejected, (state, action) => {
      state.serviceTicketDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // POST SERVICE TICKET COMMENT
    builder.addCase(postServiceTicketComment.pending, state => {
      state.serviceTicketCommentPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postServiceTicketComment.fulfilled, (state, action) => {
      state.serviceTicketCommentPostStatus = FETCH_STATUS.FULFILLED;

      // Only show success message if it's not a status update comment and not a system-generated comment
      const payload = action.payload as any;
      if (!payload.isStatusUpdateComment && !payload.isSystemGeneratedComment) {
        setTimeout(() => {
          snackbarUtils.success('Comment added successfully');
        });
      }
    });
    builder.addCase(postServiceTicketComment.rejected, (state, action) => {
      state.serviceTicketCommentPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // POST SERVICE TICKET NOTE
    builder.addCase(postServiceTicketNote.pending, state => {
      state.serviceTicketNotePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postServiceTicketNote.fulfilled, (state, action) => {
      state.serviceTicketNotePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Note added successfully');
      });
    });
    builder.addCase(postServiceTicketNote.rejected, (state, action) => {
      state.serviceTicketNotePostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // GET SERVICE TICKET NOTES
    builder.addCase(getServiceTicketNotes.pending, state => {
      state.serviceTicketNotesFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceTicketNotes.fulfilled, (state, action) => {
      state.serviceTicketNotesFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketNotes = action.payload || [];
    });
    builder.addCase(getServiceTicketNotes.rejected, (state, action) => {
      state.serviceTicketNotesFetchStatus = FETCH_STATUS.REJECTED;
      state.serviceTicketNotes = [];
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // GET SERVICE TICKET ACTION LOGS
    builder.addCase(getServiceTicketActionLogs.pending, state => {
      state.serviceTicketActionLogsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceTicketActionLogs.fulfilled, (state, action) => {
      state.serviceTicketActionLogsFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketActionLogs = action.payload || [];
    });
    builder.addCase(getServiceTicketActionLogs.rejected, (state, action) => {
      state.serviceTicketActionLogsFetchStatus = FETCH_STATUS.REJECTED;
      state.serviceTicketActionLogs = [];
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // POST SERVICE TICKET TECHNICIAN
    builder.addCase(postServiceTicketTechnician.pending, state => {
      state.serviceTicketTechnicianPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postServiceTicketTechnician.fulfilled, (state, action) => {
      state.serviceTicketTechnicianPostStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(postServiceTicketTechnician.rejected, (state, action) => {
      state.serviceTicketTechnicianPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // DELETE SERVICE TICKET TECHNICIAN
    builder.addCase(deleteServiceTicketTechnician.pending, state => {
      state.serviceTicketTechnicianPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteServiceTicketTechnician.fulfilled, (state, action) => {
      state.serviceTicketTechnicianPostStatus = FETCH_STATUS.FULFILLED;
      // Note: We're handling success messages in the component instead
    });
    builder.addCase(deleteServiceTicketTechnician.rejected, (state, action) => {
      state.serviceTicketTechnicianPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // UPDATE SERVICE TICKET SCHEDULED DATE AND TIME
    builder.addCase(updateServiceTicketScheduledDateTime.pending, state => {
      state.serviceTicketScheduledDateTimeUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateServiceTicketScheduledDateTime.fulfilled, (state, action) => {
      state.serviceTicketScheduledDateTimeUpdateStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
    });
    builder.addCase(updateServiceTicketScheduledDateTime.rejected, (state, action) => {
      state.serviceTicketScheduledDateTimeUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // DELETE SERVICE TICKET SCHEDULED DATE AND TIME
    builder.addCase(deleteServiceTicketScheduledDateTime.pending, state => {
      state.serviceTicketScheduledDateTimeDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteServiceTicketScheduledDateTime.fulfilled, (state, action) => {
      state.serviceTicketScheduledDateTimeDeleteStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Service call schedule removed successfully');
      });
    });
    builder.addCase(deleteServiceTicketScheduledDateTime.rejected, (state, action) => {
      state.serviceTicketScheduledDateTimeDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // ADD SERVICE TICKET CONTACT
    builder.addCase(addServiceTicketContact.pending, state => {
      state.serviceTicketContactAddStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(addServiceTicketContact.fulfilled, (state, action) => {
      state.serviceTicketContactAddStatus = FETCH_STATUS.FULFILLED;
      if (state.serviceTicketDetails) {
        state.serviceTicketDetails.contacts = action.payload.contacts || state.serviceTicketDetails.contacts;
      }
      setTimeout(() => {
        snackbarUtils.success('Contact added successfully');
      });
    });
    builder.addCase(addServiceTicketContact.rejected, (state, action) => {
      state.serviceTicketContactAddStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // UPDATE SERVICE TICKET CONTACT
    builder.addCase(updateServiceTicketContact.pending, state => {
      state.serviceTicketContactUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateServiceTicketContact.fulfilled, (state, action) => {
      state.serviceTicketContactUpdateStatus = FETCH_STATUS.FULFILLED;
      if (state.serviceTicketDetails) {
        state.serviceTicketDetails.contacts = action.payload.contacts || state.serviceTicketDetails.contacts;
      }
      setTimeout(() => {
        snackbarUtils.success('Contact updated successfully');
      });
    });
    builder.addCase(updateServiceTicketContact.rejected, (state, action) => {
      state.serviceTicketContactUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // DELETE SERVICE TICKET CONTACT
    builder.addCase(deleteServiceTicketContact.pending, state => {
      state.serviceTicketContactDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteServiceTicketContact.fulfilled, (state, action) => {
      state.serviceTicketContactDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Contact removed successfully');
      });
    });
    builder.addCase(deleteServiceTicketContact.rejected, (state, action) => {
      state.serviceTicketContactDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // ADD SERVICE TICKET CC USER
    builder.addCase(addServiceTicketCCUser.pending, state => {
      state.serviceTicketCCUserAddStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(addServiceTicketCCUser.fulfilled, (state, action) => {
      state.serviceTicketCCUserAddStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('CC user added successfully');
      });
    });
    builder.addCase(addServiceTicketCCUser.rejected, (state, action) => {
      state.serviceTicketCCUserAddStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // REMOVE SERVICE TICKET CC USER
    builder.addCase(removeServiceTicketCCUser.pending, state => {
      state.serviceTicketCCUserRemoveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(removeServiceTicketCCUser.fulfilled, (state, action) => {
      state.serviceTicketCCUserRemoveStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('CC user removed successfully');
      });
    });
    builder.addCase(removeServiceTicketCCUser.rejected, (state, action) => {
      state.serviceTicketCCUserRemoveStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // GET TECHNICIANS
    builder.addCase(getTechnicians.pending, state => {
      state.techniciansFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTechnicians.fulfilled, (state, action) => {
      state.techniciansFetchStatus = FETCH_STATUS.FULFILLED;
      state.technicians = action.payload;
    });
    builder.addCase(getTechnicians.rejected, (state, action) => {
      state.techniciansFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    builder.addCase(getTechnicianServiceZipCodes.pending, state => {
      state.technicianServiceZipCodesFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTechnicianServiceZipCodes.fulfilled, (state, action) => {
      state.technicianServiceZipCodesFetchStatus = FETCH_STATUS.FULFILLED;
      state.technicianServiceZipCodes = action.payload;
    });
    builder.addCase(getTechnicianServiceZipCodes.rejected, (state, action) => {
      state.technicianServiceZipCodesFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // ADD TECHNICIAN
    builder.addCase(addTechnician.pending, state => {
      state.technicianPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(addTechnician.fulfilled, (state, action) => {
      state.technicianPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Technician added successfully');
      });
    });
    builder.addCase(addTechnician.rejected, (state, action) => {
      state.technicianPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // REMOVE TECHNICIAN
    builder.addCase(removeTechnician.pending, state => {
      state.technicianRemoveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(removeTechnician.fulfilled, (state, action) => {
      state.technicianRemoveStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Technician removed successfully');
      });
    });
    builder.addCase(removeTechnician.rejected, (state, action) => {
      state.technicianRemoveStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // UPDATE TECHNICIAN
    builder.addCase(updateTechnician.pending, state => {
      state.technicianUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateTechnician.fulfilled, (state, action) => {
      state.technicianUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Technician updated successfully');
      });
    });
    builder.addCase(updateTechnician.rejected, (state, action) => {
      state.technicianUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // GET SERVICE ORGANIZATIONS
    builder.addCase(getServiceOrganizations.pending, state => {
      state.serviceOrganizationsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceOrganizations.fulfilled, (state, action) => {
      state.serviceOrganizationsFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceOrganizations = action.payload;
    });
    builder.addCase(getServiceOrganizations.rejected, (state, action) => {
      state.serviceOrganizationsFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // ADD SERVICE ORGANIZATION
    builder.addCase(addServiceOrganization.pending, state => {
      state.serviceOrganizationPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(addServiceOrganization.fulfilled, (state, action) => {
      state.serviceOrganizationPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Service organization added successfully');
      });
    });
    builder.addCase(addServiceOrganization.rejected, (state, action) => {
      state.serviceOrganizationPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // REMOVE SERVICE ORGANIZATION
    builder.addCase(removeServiceOrganization.pending, state => {
      state.serviceOrganizationRemoveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(removeServiceOrganization.fulfilled, (state, action) => {
      state.serviceOrganizationRemoveStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Service organization removed successfully');
      });
    });
    builder.addCase(removeServiceOrganization.rejected, (state, action) => {
      state.serviceOrganizationRemoveStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // UPDATE SERVICE ORGANIZATION
    builder.addCase(updateServiceOrganization.pending, state => {
      state.serviceOrganizationUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateServiceOrganization.fulfilled, (state, action) => {
      state.serviceOrganizationUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Service organization updated successfully');
      });
    });
    builder.addCase(updateServiceOrganization.rejected, (state, action) => {
      state.serviceOrganizationUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // CANCEL SERVICE TICKET
    builder.addCase(cancelServiceTicket.pending, state => {
      state.serviceTicketCancelStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(cancelServiceTicket.fulfilled, (state, action) => {
      state.serviceTicketCancelStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Service ticket cancelled successfully');
      });
    });
    builder.addCase(cancelServiceTicket.rejected, (state, action) => {
      state.serviceTicketCancelStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    // ADD SERVICE TICKET ASSIGNED USER
    builder.addCase(addServiceTicketAssignedUser.pending, state => {
      state.serviceTicketAssignedUserAddStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(addServiceTicketAssignedUser.fulfilled, (state, action) => {
      state.serviceTicketAssignedUserAddStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Assigned user added successfully');
      });
    });
    builder.addCase(addServiceTicketAssignedUser.rejected, (state, action) => {
      state.serviceTicketAssignedUserAddStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // REMOVE SERVICE TICKET ASSIGNED USER
    builder.addCase(removeServiceTicketAssignedUser.pending, state => {
      state.serviceTicketAssignedUserRemoveStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(removeServiceTicketAssignedUser.fulfilled, (state, action) => {
      state.serviceTicketAssignedUserRemoveStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Assigned user removed successfully');
      });
    });
    builder.addCase(removeServiceTicketAssignedUser.rejected, (state, action) => {
      state.serviceTicketAssignedUserRemoveStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // UPDATE SERVICE TICKET DETAILS
    builder.addCase(updateServiceTicketDetails.pending, state => {
      state.serviceTicketDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateServiceTicketDetails.fulfilled, (state, action) => {
      state.serviceTicketDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketDetails = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Service ticket details updated successfully');
      });
    });
    builder.addCase(updateServiceTicketDetails.rejected, (state, action) => {
      state.serviceTicketDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    builder.addCase(getChecklistTemplates.pending, state => {
      state.checklistTemplatesFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getChecklistTemplates.fulfilled, (state, action) => {
      state.checklistTemplatesFetchStatus = FETCH_STATUS.FULFILLED;
      state.checklistTemplates = action.payload || [];
    });
    builder.addCase(getChecklistTemplates.rejected, (state, action) => {
      state.checklistTemplatesFetchStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });

    builder.addCase(getChecklistTemplate.pending, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getChecklistTemplate.fulfilled, (state, action) => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.FULFILLED;
      state.selectedChecklistTemplate = action.payload;
    });
    builder.addCase(getChecklistTemplate.rejected, (state, action) => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    builder.addCase(createChecklistTemplate.pending, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(createChecklistTemplate.fulfilled, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Checklist template created successfully'));
    });
    builder.addCase(createChecklistTemplate.rejected, (state, action) => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    builder.addCase(updateChecklistTemplate.pending, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateChecklistTemplate.fulfilled, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Checklist template updated successfully'));
    });
    builder.addCase(updateChecklistTemplate.rejected, (state, action) => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    builder.addCase(deleteChecklistTemplate.fulfilled, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Checklist template deleted successfully'));
    });
    builder.addCase(setDefaultChecklistTemplate.fulfilled, state => {
      state.checklistTemplateMutateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Default checklist template updated'));
    });

    builder.addCase(getServiceTicketChecklist.pending, state => {
      state.serviceTicketChecklistFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceTicketChecklist.fulfilled, (state, action) => {
      state.serviceTicketChecklistFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketChecklist = action.payload || null;
    });
    builder.addCase(getServiceTicketChecklist.rejected, (state, action) => {
      state.serviceTicketChecklistFetchStatus = FETCH_STATUS.REJECTED;
      state.serviceTicketChecklist = null;
    });

    builder.addCase(updateServiceTicketChecklistItem.pending, state => {
      state.serviceTicketChecklistMutateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateServiceTicketChecklistItem.fulfilled, (state, action) => {
      state.serviceTicketChecklistMutateStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketChecklist = action.payload || state.serviceTicketChecklist;
    });
    builder.addCase(updateServiceTicketChecklistItem.rejected, (state, action) => {
      state.serviceTicketChecklistMutateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });

    builder.addCase(completeServiceTicketChecklist.pending, state => {
      state.serviceTicketChecklistMutateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(completeServiceTicketChecklist.fulfilled, (state, action) => {
      state.serviceTicketChecklistMutateStatus = FETCH_STATUS.FULFILLED;
      state.serviceTicketChecklist = action.payload || state.serviceTicketChecklist;
      setTimeout(() => snackbarUtils.success('Checklist completed successfully'));
    });
    builder.addCase(completeServiceTicketChecklist.rejected, (state, action) => {
      state.serviceTicketChecklistMutateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
  },
});

export const {
  setServiceTicketsList,
  setServiceTicketsListFetchStatus,
  setServiceTicketCommentPostStatus,
  setServiceTicketNotePostStatus,
  setTechnicians,
  setTechniciansFetchStatus,
  setServiceOrganizations,
  setServiceOrganizationsFetchStatus,
  setChecklistTemplatesList,
  setChecklistTemplatesListFetchStatus,
} = serviceCallsSlice.actions;

export default serviceCallsSlice.reducer;
